import { useCallback, useEffect, useState, useSyncExternalStore, type FC } from 'react';
import { useSubscription } from '@pages/Subscriptionsv2/hooks/useSubscriptions';
import { useFeatureVersion } from '@hooks/useFeatureVersion';
import { FeatureVersionCard } from './FeatureVersionCard';
import { FeatureInvitationModal } from './FeatureInvitationModal';
import { ComparePeekBar } from './ComparePeekBar';
import { EvacuatedNotice } from './EvacuatedNotice';
import { GaAnnouncementBanner } from './GaAnnouncementBanner';
import { RevertFeedbackPrompt, type FeedbackOption } from './RevertFeedbackPrompt';
import { startPreview } from '@utils/featureRollout/preview';
import { getActiveCommerceId } from '@utils/featureRollout/cache';
import { isRoleRejected, subscribeRoleRejection } from '@utils/featureRollout/roleRejection';
import {
  clearRevertFeedback,
  isRevertFeedbackRequested,
  subscribeRevertFeedback,
} from '@utils/featureRollout/revertFeedback';
import {
  clearPermanenceSurvey,
  isPermanenceSurveyRequested,
  subscribePermanenceSurvey,
} from '@utils/featureRollout/permanenceSurvey';
import {
  clearTransitionSurvey,
  getSunsetDate,
  isTransitionSurveyRequested,
  subscribeTransitionSurvey,
} from '@utils/featureRollout/transitionSurvey';
import { optOutUsageFeedback } from '@utils/featureRollout/preview';
import { MENU_FEATURE_KEY } from '@utils/featureRollout/constants';
import { PERMANENCE_SURVEY, REVERT_FEEDBACK, TRANSITION_SURVEY } from '@utils/featureRollout/strings';
import { OVERLAY_PRIORITY, useOverlaySlot } from '@context/overlayGate';
import { Z } from '@components/v4/zIndex';

// Conector del framework de rollout para `menu-v2`: cablea el hook + la identidad root
// con la card reutilizable y el modal contextual de primera vez. Se monta en el shell del
// menú (sobre la página resuelta), tanto en V1 (control) como en V2 (treatment).
//
// CADENCIA SERVER-SIDE (contrato §8): el FE ya NO recalcula con menu_state. OBEDECE
// `inviteSurface` que deriva el backend (cooldown 21d→45d→sticky, cap 3, snooze, next_eligible).
// La card/modal son el canal PUSH y se gobiernan EXCLUSIVAMENTE por inviteSurface:
//   - 'modal' → modal contextual + card (primera vez, never_tried).
//   - 'card'  → solo la card discreta (recordatorio dentro del cap).
//   - 'none'  → no invitar PUSH (sticky | GA | evacuado | snooze | next_eligible futuro).
//
// DEDUP del CTA en control: el chip PULL (MenuVersionStatusChip) y la card PUSH NO se solapan
// porque el chip solo aparece con inviteSurface==='none'. Mientras la card está activa
// (modal/card) ella muestra el único CTA; al descartar/snoozear (inviteSurface→'none') el chip
// reaparece como entrada discreta. Aquí también se RENDERIZA el prompt de feedback al revertir
// (pedido por el chip de status vía signal), que debe sobrevivir al desmontaje de la página V2.
//
// El switch durable lo dispara el root EFECTIVO (isRoot del FE y SIN rechazo de rol del backend
// esta sesión); no-root o rol-rechazado → preview per-usuario (card y modal lo manejan).

const MenuVersionInvitation: FC = () => {
  const {
    variant,
    canRevert,
    gaForced,
    inviteSurface,
    operatorEvacuated,
    evacReason,
    compareActive,
    switchTo,
    revert,
    optOut,
    snooze,
    markInvitationShown,
    submitRevertFeedback,
    submitPermanenceFeedback,
    menuState,
    feedbackOptions,
  } = useFeatureVersion(MENU_FEATURE_KEY);
  const { isRoot } = useSubscription();

  // Catálogo de chips per-experimento (anexo feedback_options) con fallback a strings.ts.
  // 'Otro' SIEMPRE presente y SIEMPRE al final — lo agrega el FE, no el catálogo (§3): garantiza
  // el escape hatch (el submit requiere chip seleccionado) y la posición estable. Claves
  // independientes: revert con catálogo y permanence con defaults pueden convivir.
  const OTHER: FeedbackOption = { value: 'other', label: 'Otro' };
  const revertOptions = feedbackOptions?.revert ? [...feedbackOptions.revert, OTHER] : REVERT_FEEDBACK.options;
  const permanenceOptions = feedbackOptions?.permanence
    ? [...feedbackOptions.permanence, OTHER]
    : PERMANENCE_SURVEY.options;

  // Rechazo de rol del backend (REQUIRES_ROOT) en esta sesión: si está, el usuario NO es root
  // efectivo aunque el FE lo crea → la card/modal ofrecen preview en vez del CTA durable (rompe
  // el loop). Mismo criterio que MenuVersionStatusChip.
  const subscribeRole = useCallback((cb: () => void) => subscribeRoleRejection(MENU_FEATURE_KEY, cb), []);
  const getRoleRejected = useCallback(() => isRoleRejected(getActiveCommerceId(), MENU_FEATURE_KEY), []);
  const roleRejected = useSyncExternalStore(subscribeRole, getRoleRejected, getRoleRejected);
  const effectiveRoot = Boolean(isRoot) && !roleRejected;

  // Prompt de feedback al revertir (D1): el chip de status lo PIDE vía signal (al revertir, su
  // página treatment se desmonta); este componente vive en el shell y persiste el flip, así que
  // es quien lo RENDERIZA. Omitible y fire-and-forget; nunca bloquea ni revierte el cambio.
  const subscribeFeedback = useCallback((cb: () => void) => subscribeRevertFeedback(MENU_FEATURE_KEY, cb), []);
  const getFeedbackRequested = useCallback(() => isRevertFeedbackRequested(MENU_FEATURE_KEY), []);
  const feedbackRequested = useSyncExternalStore(subscribeFeedback, getFeedbackRequested, getFeedbackRequested);
  // onSubmit SOLO envía la telemetría (fire-and-forget); NO limpia el signal aquí. Si limpiáramos
  // síncronamente, `feedbackRequested` pasaría a false en el mismo batch que el setSent(true) interno
  // del prompt → el padre desmontaría el RevertFeedbackPrompt antes de que su pantalla de
  // agradecimiento ("thank you", sent===true) alcance a renderizar (Seer). El cierre se difiere al
  // onSkip: el prompt muestra el agradecimiento con su botón X (→ onSkip → clear) y el backdrop-click
  // también cierra. Así el usuario ve la confirmación y luego se desmonta.
  const onFeedbackSubmit = (reason: string, text: string) => {
    submitRevertFeedback(reason, text, 'revert_feedback');
  };
  const onFeedbackSkip = () => clearRevertFeedback(MENU_FEATURE_KEY);

  // Encuesta de PERMANENCIA (adopción/quedarse): el CTA "Quedarme" o el disparador pasivo
  // (menu_state='adopted') la PIDEN vía signal; este componente del shell la RENDERIZA como overlay.
  // trigger_source ∈ {adopted, kept}: si el backend ya resolvió 'adopted' (vía pasivo/uso sostenido)
  // lo marcamos 'adopted'; si no, vino del CTA explícito → 'kept'. La procedencia vive en el evento.
  const subscribePermanence = useCallback((cb: () => void) => subscribePermanenceSurvey(MENU_FEATURE_KEY, cb), []);
  const getPermanenceRequested = useCallback(() => isPermanenceSurveyRequested(MENU_FEATURE_KEY), []);
  const permanenceRequested = useSyncExternalStore(subscribePermanence, getPermanenceRequested, getPermanenceRequested);
  const permanenceTrigger = menuState === 'adopted' ? 'adopted' : 'kept';
  // onSubmit SOLO envía la telemetría; el cierre se difiere al onSkip (mismo motivo que el feedback
  // de reversión arriba): limpiar el signal síncronamente desmontaría el prompt en el mismo batch que
  // su setSent(true), tragándose la pantalla de agradecimiento (Seer). El prompt muestra el "thank
  // you" con su X (→ onPermanenceSkip → clear) y el backdrop-click también cierra.
  const onPermanenceSubmit = (reason: string, text: string) => {
    submitPermanenceFeedback(reason, text, permanenceTrigger);
  };
  const onPermanenceSkip = () => clearPermanenceSurvey(MENU_FEATURE_KEY);
  // "No volver a preguntar": opt-out DURABLE per-usuario (misma llave dedicada que el feedback por
  // uso), luego cierra. No reaparecerá la encuesta de permanencia para este operador.
  const onPermanenceOptOut = () => {
    optOutUsageFeedback(MENU_FEATURE_KEY);
    clearPermanenceSurvey(MENU_FEATURE_KEY);
  };

  // SUNSET: aviso de retiro de la versión vieja (v1) + encuesta de transición. La fecha y el signal
  // viven en transitionSurvey (seteados por el GET, desacoplados del cache de versión). El banner es
  // inline; la encuesta es un overlay clon del de permanencia. El feedback viaja como 'usage_feedback'
  // con trigger 'lifecycle_transition' → NUNCA alimenta el KPI/decisión del experimento.
  const subscribeTransition = useCallback((cb: () => void) => subscribeTransitionSurvey(MENU_FEATURE_KEY, cb), []);
  const getSunset = useCallback(() => getSunsetDate(MENU_FEATURE_KEY), []);
  const sunsetDate = useSyncExternalStore(subscribeTransition, getSunset, getSunset);
  const getTransitionRequested = useCallback(() => isTransitionSurveyRequested(MENU_FEATURE_KEY), []);
  const transitionRequested = useSyncExternalStore(subscribeTransition, getTransitionRequested, getTransitionRequested);
  const onTransitionSubmit = (reason: string, text: string) => {
    submitPermanenceFeedback(reason, text, 'lifecycle_transition');
  };
  const onTransitionSkip = () => clearTransitionSurvey(MENU_FEATURE_KEY);
  const onTransitionOptOut = () => {
    optOutUsageFeedback(MENU_FEATURE_KEY);
    clearTransitionSurvey(MENU_FEATURE_KEY);
  };

  // Item E#4 — el CTA decide por ROL EN RENDER. Mientras isRoot NO esté resuelto (undefined antes
  // de /v1/auth/account-details) NO mostramos un CTA potencialmente equivocado (un root vería el
  // CTA de preview por el breve instante en que isRoot=false-por-defecto). En vez de un spinner de
  // PANTALLA ENTERA, simplemente NO montamos la card/modal hasta resolver: el menú ya se ve.
  const roleResolved = isRoot !== undefined;

  // Superficies derivadas del backend (no heurística local):
  //   showCard = inviteSurface ∈ {modal, card}; showModal = inviteSurface === 'modal'.
  // En treatment NO se invita (la card de invitación es solo para control).
  const showCard = roleResolved && (inviteSurface === 'modal' || inviteSurface === 'card') && variant !== 'treatment';
  const canOpenModal = roleResolved && inviteSurface === 'modal' && variant !== 'treatment';

  // El modal se abre una vez por montaje del shell si el backend lo indica (inviteSurface=modal).
  // Decisión SÍNCRONA (el invite_surface ya viaja en el cache hidratado) → sin parpadeo.
  const [showModal, setShowModal] = useState(() => canOpenModal);

  // inviteSurface puede llegar después de hidratar. Si pasa a 'modal', abrimos una sola vez.
  const openedRef = useState(() => ({ done: false }))[0];
  useEffect(() => {
    if (openedRef.done) return;
    if (canOpenModal) {
      openedRef.done = true;
      setShowModal(true);
    }
  }, [canOpenModal, openedRef]);

  // OverlayGate: the auto-shown invitation modal claims the `featureInvitation` slot so it never
  // stacks on top of (or under) another interruptive overlay and yields to an incoming order.
  // It is NOT counted against the per-session OS-nag cap (experiment exposure must not be lost).
  const inviteGranted = useOverlaySlot(
    'menu-version-invite',
    OVERLAY_PRIORITY.featureInvitation,
    showModal && !operatorEvacuated,
  );

  const closeModal = () => setShowModal(false);
  // "No volver a preguntar" → opt_out (el backend la vuelve sticky).
  const neverAsk = () => {
    optOut('menu_modal');
    setShowModal(false);
  };
  // "Recordármelo más tarde" → snooze 7 días (el backend suprime hasta entonces).
  const remindLater = () => {
    snooze(7, 'menu_modal');
    setShowModal(false);
  };
  const acceptFromModal = () => {
    switchTo('treatment', 'menu_modal');
    setShowModal(false);
  };
  // NO-ROOT: "echa un vistazo" → vista previa per-usuario (no toca el comercio ni el switch
  // durable). El gate del menú lee isPreviewActive y renderiza V2 solo para este usuario/tab.
  const previewFromModal = () => {
    startPreview(MENU_FEATURE_KEY);
    setShowModal(false);
  };
  const previewFromCard = () => startPreview(MENU_FEATURE_KEY);

  return (
    <>
      {/* Prompt de feedback al revertir (D1). Pedido por el chip de status vía signal; se renderiza
          aquí (shell persistente) porque al revertir la página treatment —y su chip— se desmonta.
          OVERLAY fijo (mismo patrón que FeatureInvitationModal): NO va en el flujo del documento
          para no EMPUJAR el contenido del menú; flota centrado sobre la página. Backdrop-click =
          omitir (fire-and-forget, no bloquea). */}
      {feedbackRequested && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(17,26,34,.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            zIndex: Z.overlayPrompt,
            fontFamily: 'inherit',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) onFeedbackSkip();
          }}
        >
          <div style={{ width: '100%', maxWidth: 420 }}>
            <RevertFeedbackPrompt options={revertOptions} onSubmit={onFeedbackSubmit} onSkip={onFeedbackSkip} />
          </div>
        </div>
      )}

      {/* Encuesta de PERMANENCIA (adopción/quedarse). Reusa RevertFeedbackPrompt con opciones
          POSITIVAS + abierto "¿qué le falta?". OVERLAY fijo (no empuja el contenido del menú):
          flota centrado sobre la página, mismo patrón que el prompt de reversión. Backdrop-click =
          omitir (fire-and-forget). Omitible y con opt-out durable ("No volver a preguntar"). */}
      {permanenceRequested && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(17,26,34,.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            zIndex: Z.overlayPrompt,
            fontFamily: 'inherit',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) onPermanenceSkip();
          }}
        >
          <div style={{ width: '100%', maxWidth: 420 }}>
            <RevertFeedbackPrompt
              title={PERMANENCE_SURVEY.title}
              subtitle={PERMANENCE_SURVEY.subtitle}
              options={permanenceOptions}
              textPlaceholder={PERMANENCE_SURVEY.textPlaceholder}
              onSubmit={onPermanenceSubmit}
              onSkip={onPermanenceSkip}
              onOptOut={onPermanenceOptOut}
              optOutLabel={PERMANENCE_SURVEY.optOut}
            />
          </div>
        </div>
      )}

      {/* Encuesta de TRANSICIÓN (SUNSET): se le pide al comercio v1 que está por perder su versión.
          Mismo overlay/patrón que la de permanencia; opciones genéricas (TRANSITION_SURVEY). Omitible
          (backdrop-click) y con opt-out durable. El feedback NO toca el KPI del experimento. */}
      {transitionRequested && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(17,26,34,.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            zIndex: Z.overlayPrompt,
            fontFamily: 'inherit',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) onTransitionSkip();
          }}
        >
          <div style={{ width: '100%', maxWidth: 420 }}>
            <RevertFeedbackPrompt
              title={TRANSITION_SURVEY.title}
              subtitle={TRANSITION_SURVEY.subtitle}
              options={TRANSITION_SURVEY.options}
              textPlaceholder={TRANSITION_SURVEY.textPlaceholder}
              onSubmit={onTransitionSubmit}
              onSkip={onTransitionSkip}
              onOptOut={onTransitionOptOut}
              optOutLabel={TRANSITION_SURVEY.optOut}
            />
          </div>
        </div>
      )}

      {/* Ramp-down: el operador nos regresó a la anterior. No invitamos; copy REASON-AWARE
          (temporal vs descontinuado) para que el comercio entienda qué pasó. */}
      {operatorEvacuated && (
        <div style={{ marginBottom: 12 }}>
          <EvacuatedNotice reason={evacReason} />
        </div>
      )}

      {/* Anuncio GA (C2): el comercio ya está graduado (la versión nueva es la oficial). Banner
          descartable tipo "nueva funcionalidad", no intrusivo. Se auto-oculta si ya fue descartado.
          No se muestra durante un peek (la barra de comparación tiene prioridad). */}
      {gaForced && !operatorEvacuated && !compareActive && (
        <div style={{ marginBottom: 12 }}>
          <GaAnnouncementBanner featureKey={MENU_FEATURE_KEY} />
        </div>
      )}

      {/* Aviso de RETIRO (SUNSET): el comercio sigue en v1 (control) y hay fecha de retiro programada.
          Banner inline con la fecha (dismiss durable propio). No se muestra durante un peek ni si fue
          evacuado por el operador. */}
      {sunsetDate && variant === 'control' && !gaForced && !operatorEvacuated && !compareActive && (
        <div style={{ marginBottom: 12 }}>
          <GaAnnouncementBanner featureKey={MENU_FEATURE_KEY} sunsetDate={sunsetDate} />
        </div>
      )}

      {/* Barra sticky de comparación: visible solo durante un peek (compare_active). Vive aquí
          (shell del menú) porque durante el peek la variante resuelta es V1 (control). */}
      {compareActive && (
        <ComparePeekBar onReturn={() => switchTo('treatment', 'compare_return')} isRoot={effectiveRoot} />
      )}

      {/* INVITACIÓN (control): la card solo si el backend la habilita (modal/card) y no estamos
          evacuados ni comparando. En treatment el indicador vive como CHIP (MenuVersionStatusChip). */}
      {showCard && !operatorEvacuated && !compareActive && (
        <div style={{ marginBottom: 12 }}>
          <FeatureVersionCard
            variant={variant}
            canRevert={canRevert}
            gaForced={gaForced}
            isRoot={effectiveRoot}
            onTry={() => switchTo('treatment', 'menu_shell')}
            onPreview={previewFromCard}
            onRevert={() => revert('revert_commit')}
            onShown={() => markInvitationShown('menu_shell')}
          />
        </div>
      )}

      {showModal && !operatorEvacuated && inviteGranted && (
        <FeatureInvitationModal
          isRoot={effectiveRoot}
          onTry={acceptFromModal}
          onPreview={previewFromModal}
          onDismiss={closeModal}
          onRemindLater={remindLater}
          onNeverAsk={neverAsk}
          onShown={() => markInvitationShown('menu_modal')}
        />
      )}
    </>
  );
};

export default MenuVersionInvitation;
